import type {
  ReportLayoutConfig,
  ReportSectionTabConfig,
  SectionBuild,
} from '../models/layoutConfig.types'

// v3 `configs/CreativePerformance/CreativeDashboard.js` (361 ln) — THE card-mode layout.
// Port notes beyond the standard table:
// - Section `visibleType: 'card'` → `view: 'card'`; v3 `cardButton: activeType !== 'Text'`
//   means only the Text variant drops 'card' from `allowedViews`.
// - v3 always requested the 'Overall' partition list for the active media type and narrowed
//   rows with the `marketing_channel` filter; the per-channel lists only gate the rail. The
//   one exception is Ad Level Overview (`edges`): report 66 against the tab's own ad tabs.
// - v3 `showCols`/`addMetrics` (custom_conversion_1..5)/`addDimensions` wait for ORP-18643
//   column shaping. `showLevels`, `hideTotal`/`orderedMetrics` (Benchmark-only — that page
//   never renders in this layout), `hideChartButton`, `fieldValues`, and return-`channel`
//   are dropped. `chartSortMetric: 'cost'` folds into `sortField`.

const CREATIVE_REPORT_ID = 13
const CREATIVE_AD_REPORT_ID = 66

type CreativeMediaType = 'Overview' | 'Image' | 'Video' | 'Text'

// v3 `CREATIVE_DASHBOARD_DETAILS[0]` ('Overall') — the request target sets.
const OVERALL_IMAGE_TABS = [
  'image_overview',
  'google_ads_image',
  'meta_ads_image',
  'tiktok_ads_image',
  'linkedin_ads_image',
  'snapchat_image',
  'criteo_image',
  'rtb_house_image',
  'reddit_ads_image',
  'linkedin_image',
  'twitter_ads_image',
  'pinterest_ads_image',
  'dv360_image',
]

// (sic) v3 lists 'linkedin_ads_video' twice and never 'linkedin_video' — kept verbatim, so
// the linkedin_video partition stays out of the Overall video request exactly as in v3.
const OVERALL_VIDEO_TABS = [
  'video_overview',
  'google_ads_video',
  'meta_ads_video',
  'tiktok_ads_video',
  'linkedin_ads_video',
  'snapchat_video',
  'criteo_video',
  'rtb_house_video',
  'reddit_ads_video',
  'linkedin_ads_video',
  'twitter_ads_video',
  'pinterest_ads_video',
  'dv360_video',
]

const OVERALL_OVERVIEW_TABS = [
  'video_overview',
  'google_ads_image',
  'google_ads_video',
  'Meta Ad Level Image',
  'Meta Ad Level Video',
  'meta_ads_image',
  'meta_ads_video',
  'tiktok_ads_image',
  'tiktok_ads_video',
  'linkedin_ads_image',
  'linkedin_ads_video',
  'linkedin_image',
  'linkedin_video',
  'snapchat_image',
  'snapchat_video',
  'criteo_image',
  'criteo_video',
  'rtb_house_image',
  'rtb_house_video',
  'reddit_ads_image',
  'reddit_ads_video',
  'twitter_ads_image',
  'twitter_ads_video',
  'pinterest_ads_image',
  'pinterest_ads_video',
  'dv360_image',
  'dv360_video',
]

const OVERALL_TEXT_TABS = ['google_ads_adtext', 'meta_ads_adtext']

const OVERALL_REQUEST_TABS: Record<CreativeMediaType, string[]> = {
  Overview: OVERALL_OVERVIEW_TABS,
  Image: OVERALL_IMAGE_TABS,
  Video: OVERALL_VIDEO_TABS,
  Text: OVERALL_TEXT_TABS,
}

interface CreativeChannelDetail {
  name: string
  channel?: string
  image: string[]
  video: string[]
  /** v3 hid rail entries without a Text list — only Overall/Google/Meta carry one. */
  text?: string[]
}

// v3 `CREATIVE_DASHBOARD_DETAILS` — per-channel rail gating lists (DV360 was commented out
// in v3; the per-channel Overview/Benchmark lists are unused by this layout and drop).
const CREATIVE_CHANNEL_DETAILS: CreativeChannelDetail[] = [
  {
    name: 'Overall',
    image: OVERALL_IMAGE_TABS,
    video: OVERALL_VIDEO_TABS,
    text: OVERALL_TEXT_TABS,
  },
  {
    name: 'Google',
    channel: 'google_ads',
    image: ['google_ads_image'],
    video: ['google_ads_video'],
    text: ['google_ads_adtext'],
  },
  {
    name: 'Meta',
    channel: 'meta_ads',
    image: ['meta_ads_image'],
    video: ['meta_ads_video'],
    text: ['meta_ads_adtext'],
  },
  {
    name: 'Tiktok',
    channel: 'tiktok_ads',
    image: ['tiktok_ads_image'],
    video: ['tiktok_ads_video'],
  },
  {
    name: 'Linkedin',
    channel: 'linkedin',
    image: ['linkedin_ads_image', 'linkedin_image'],
    video: ['linkedin_ads_video', 'linkedin_video'],
  },
  {
    name: 'Snapchat',
    channel: 'snapchat',
    image: ['snapchat_image'],
    video: ['snapchat_video'],
  },
  {
    name: 'Criteo',
    channel: 'criteo',
    image: ['criteo_image'],
    video: ['criteo_video'],
  },
  {
    name: 'RTB',
    channel: 'rtb_house',
    image: ['rtb_house_image'],
    video: ['rtb_house_video'],
  },
  {
    name: 'Reddit',
    channel: 'reddit_ads',
    image: ['reddit_ads_image'],
    video: ['reddit_ads_video'],
  },
  {
    name: 'Twitter',
    channel: 'twitter_ads',
    image: ['twitter_ads_image'],
    video: ['twitter_ads_video'],
  },
  {
    name: 'Pinterest',
    channel: 'pinterest_ads',
    image: ['pinterest_ads_image'],
    video: ['pinterest_ads_video'],
  },
  {
    name: 'DV360',
    channel: 'dv_360',
    image: ['dv360_image', 'dv_360_image'],
    video: ['dv360_video', 'dv_360_video'],
  },
]

const mediaRailTabs = (media: 'image' | 'video' | 'text'): ReportSectionTabConfig[] =>
  CREATIVE_CHANNEL_DETAILS.flatMap((detail) => {
    const tabs = detail[media]
    if (!tabs) return []
    return [{ name: detail.name, tabs, ...(detail.channel ? { channel: detail.channel } : {}) }]
  })

// v3 Overview tabs came from `BUDGET_ALLOCATION_DETAILS` (minus dv360) with `tabs: adTabs`.
// Both v3 'Yandex' entries are kept (duplicate rail name, as in v3); the Ad Level build uses
// each tab's own `tabs` instead of v3's find-by-name (which always hit the first Yandex).
const OVERVIEW_RAIL_TABS: ReportSectionTabConfig[] = [
  { name: 'Overall', tabs: ['overview'] },
  { name: 'Google', tabs: ['google_ads_ad'], channel: 'google_ads' },
  { name: 'Meta', tabs: ['meta_ads_ad'], channel: 'meta_ads' },
  { name: 'Criteo', tabs: ['criteo_ad'], channel: 'criteo' },
  { name: 'Tiktok', tabs: ['tiktok_ads_ad'], channel: 'tiktok_ads' },
  { name: 'Linkedin', tabs: ['linkedin_ad'], channel: 'linkedin' },
  { name: 'Snapchat', tabs: ['snapchat_ads_ad'], channel: 'snapchat_ads' },
  { name: 'RTB', tabs: ['rtb_house_ad'], channel: 'rtb_house' },
  { name: 'Reklamstore', tabs: ['reklamstore_ad'], channel: 'reklamstore' },
  { name: 'Akakce', tabs: ['akakce_ad'], channel: 'akakce' },
  { name: 'Gelirortaklari', tabs: ['gelirortaklari_ad'], channel: 'gelirortaklari' },
  { name: 'Yandex', tabs: ['yandex_ad'], channel: 'yandex' },
  { name: 'Apple Search Ads', tabs: ['apple_search_ads_ad'], channel: 'apple_search_ads' },
  { name: 'App Samurai', tabs: ['app_samurai_ad'], channel: 'app_samurai' },
  { name: 'Bing Ads', tabs: ['bing_ads_ad'], channel: 'bing_ads' },
  { name: 'Yango', tabs: ['yango_ad'], channel: 'yango' },
  { name: 'Hopi', tabs: ['hopi_ad'], channel: 'hopi' },
  { name: 'Twitter', tabs: ['twitter_ads_ad'], channel: 'twitter_ads' },
  { name: 'Yandex', tabs: ['yandex_ads_ad'], channel: 'yandex_ads' },
  { name: 'Pinterest', tabs: ['pinterest_ads_ad'], channel: 'pinterest_ads' },
  { name: 'Reddit', tabs: ['reddit_ads_ad'], channel: 'reddit_ads' },
  { name: 'CM360', tabs: ['cm360_ad'], channel: 'cm360' },
  { name: 'Trivago', tabs: ['trivago_ad'], channel: 'trivago' },
  { name: 'Dv360', tabs: ['dv360_ad'], channel: 'dv360' },
  { name: 'Cimri', tabs: ['cimri_ad'], channel: 'cimri' },
  {
    name: 'Sheet',
    tabs: ['metrics_sheet_campaign'],
    channel: 'sheet_custom_channel',
    checkDimension: { field: 'marketing_channel', value: ['sheet_custom_channel'] },
  },
]

// v3 `defaultDims` — the Overview variant's table field set per creative level.
const AD_LEVEL_DIMENSIONS = [
  'campaign_name',
  'preview_ss_url',
  'marketing_channel',
  'objective',
  'ad_name',
  'effective_status',
  'ad_last_seen',
]

const CREATIVE_LEVEL_DIMENSIONS = [
  'marketing_channel',
  'preview_ss_url',
  'image_name',
  'video_id',
  'image_id',
  'image_url',
  'thumbnail_url',
]

// v3 `setDimensions` for the Image/Video variants (Text sent none).
const CREATIVE_ASSET_DIMENSIONS = ['preview_ss_url', 'image_url', 'thumbnail_url']

// v3 `getCreativeDashboardConfig(tab, activeType, level)`. The Benchmark / Ad Library
// branches belong to CreativeBenchmarkSection (a different v3 view) and are not ported.
const buildCreativeSection =
  (activeType: CreativeMediaType): SectionBuild =>
  ({ tab, level }) => {
    // v3 `edges`: Ad Level + Overview only → report 66 against the tab's own ad partitions.
    const adLevelOverview = activeType === 'Overview' && level === 'ad'
    const reportId = adLevelOverview ? CREATIVE_AD_REPORT_ID : CREATIVE_REPORT_ID
    const dimensions =
      activeType === 'Overview'
        ? level === 'ad'
          ? AD_LEVEL_DIMENSIONS
          : CREATIVE_LEVEL_DIMENSIONS
        : activeType === 'Text'
          ? null
          : CREATIVE_ASSET_DIMENSIONS
    return {
      id: `${reportId}-${tab.name}${activeType}`,
      reportId,
      scheduleTabs: adLevelOverview ? (tab.tabs ?? []) : OVERALL_REQUEST_TABS[activeType],
      filters: tab.channel
        ? [{ field: 'marketing_channel', match_mode: 'equals', value: tab.channel }]
        : [],
      view: activeType === 'Text' ? 'table' : 'card',
      allowedViews: activeType === 'Text' ? ['table', 'chart'] : ['table', 'card', 'chart'],
      chartType: 'column',
      chartDimensions: adLevelOverview ? ['ad_name'] : ['image_url', 'thumbnail_url'],
      chartMetrics: ['cost'],
      sortField: 'cost',
      ...(dimensions ? { dimensions } : {}),
      ...(adLevelOverview ? { edges: ['image_id', 'video_id'] } : {}),
      ...(activeType === 'Image' || activeType === 'Video' ? { partitionOnly: true } : {}),
      ...(activeType === 'Video' ? { cardMediaType: 'Video' as const } : {}),
    }
  }

export const CREATIVE_DASHBOARD: ReportLayoutConfig = [
  {
    name: 'Overview',
    labelKey: 'reports.creativeDashboard.tabs.overview',
    sections: [
      {
        id: '66-creative-overview',
        reportId: CREATIVE_AD_REPORT_ID,
        creativeLevel: true,
        build: buildCreativeSection('Overview'),
        tabs: OVERVIEW_RAIL_TABS,
      },
    ],
  },
  {
    name: 'Image',
    labelKey: 'reports.creativeDashboard.tabs.image',
    sections: [
      {
        id: '13-image',
        reportId: CREATIVE_REPORT_ID,
        defaultLevel: 'creative',
        build: buildCreativeSection('Image'),
        tabs: mediaRailTabs('image'),
      },
    ],
  },
  {
    name: 'Video',
    labelKey: 'reports.creativeDashboard.tabs.video',
    sections: [
      {
        id: '13-video',
        reportId: CREATIVE_REPORT_ID,
        defaultLevel: 'creative',
        build: buildCreativeSection('Video'),
        tabs: mediaRailTabs('video'),
      },
    ],
  },
  {
    name: 'Text',
    labelKey: 'reports.creativeDashboard.tabs.text',
    sections: [
      {
        id: '13-text',
        reportId: CREATIVE_REPORT_ID,
        build: buildCreativeSection('Text'),
        tabs: mediaRailTabs('text'),
      },
      {
        id: '13-ad-texts-phrase-density',
        kind: 'pending',
        pendingNote: 'v3 isNgram → NgramChartArea (follow-up slice)',
        reportId: CREATIVE_REPORT_ID,
        hideChannels: true,
        tabs: [{ name: 'Gram', kind: 'pending', tabs: ['creative_ngram_data'] }],
      },
    ],
  },
]
